import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import styles from './App.module.css';
import { takePendingSendForThisPage } from './auth/pendingSend';
import ChatArea from './components/ChatArea';
import FeedbackButton from './components/FeedbackButton';
import OnboardingSurvey from './components/OnboardingSurvey';
import Sidebar from './components/Sidebar';
import Toast from './components/Toast';
import WelcomeScreen from './components/WelcomeScreen';
import WhatsNewDialog from './components/WhatsNewDialog';
import WhatsNewPill from './components/WhatsNewPill';
import { useChat } from './hooks/useChat';
import { useOnboardingSurvey } from './hooks/useOnboardingSurvey';
import { useProductCatalog } from './hooks/useProductCatalog';
import { useSessions } from './hooks/useSessions';
import { useUser } from './hooks/useUser';
import { useWhatsNew } from './hooks/useWhatsNew';
import type { ImageRef, SessionInfo } from './types/api';

export default function App() {
  const [collapsed, setCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(272);
  const [isChatExiting, setIsChatExiting] = useState(false);
  const [contextPanelOpen, setContextPanelOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const match = useMatch('/conversation/:sessionId');
  const currentSessionId = match?.params.sessionId ?? null;

  const {
    sessions,
    loading: sessionsLoading,
    loadingMore,
    hasMore,
    search,
    loadMore,
    refresh: refreshSessions,
    renameSession,
    deleteSession,
    justCompleted,
    clearJustCompleted,
  } = useSessions();
  const chat = useChat();
  const newSessionIdRef = useRef<string | null>(null);
  const resumingSessionRef = useRef<string | null>(null);
  const { user } = useUser();
  // Optimistic default: most deployments have this configured (it's required
  // for HISTORY_BACKEND=blob, the default), so assume enabled during the
  // brief window before /me resolves rather than flashing the button in.
  const attachmentsEnabled = user?.chat_attachments_enabled ?? true;
  const {
    products,
    comingSoon,
    loading: catalogLoading,
    error: catalogError,
    selectedSlug,
    selectProduct,
    selectedVersion,
  } = useProductCatalog();
  const [toastVisible, setToastVisible] = useState(false);
  const [toastSession, setToastSession] = useState<SessionInfo | null>(null);
  // Asked at the door rather than after a first answer, so it waits for the catalog
  // as well as the person: the tenure question names a product, and asking it before
  // the catalog resolves would word it against DHI software in general.
  const survey = useOnboardingSurvey(Boolean(user) && !catalogLoading, selectedSlug);
  const whatsNew = useWhatsNew(Boolean(user));

  // biome-ignore lint/correctness/useExhaustiveDependencies: fire-on-event pattern — react only when justCompleted changes; clearJustCompleted consumes the trigger
  useEffect(() => {
    if (!justCompleted) return;
    if (justCompleted.session_id === currentSessionId) {
      void chat.loadSession(currentSessionId);
    } else {
      setToastSession(justCompleted);
      setToastVisible(true);
    }
    clearJustCompleted();
  }, [justCompleted]);

  const showChat = location.pathname.startsWith('/conversation/');
  const [displayChat, setDisplayChat] = useState(showChat);

  useEffect(() => {
    if (location.pathname === '/') {
      navigate('/assistant', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (!showChat) {
      setDisplayChat(false);
      return;
    }
    const t = setTimeout(() => setDisplayChat(true), 140);
    return () => clearTimeout(t);
  }, [showChat]);

  // A message sent just before the page left for the silent sign-in, which the backend
  // never received (#486). It goes out again as though it had just been sent. Declared
  // before the load below, and that load skips the session while this runs: loading
  // aborts whatever is in flight, which would cancel the resend it is racing.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once per page load by design; see takePendingSendForThisPage
  useEffect(() => {
    const pending = takePendingSendForThisPage();
    if (!pending) return;
    resumingSessionRef.current = pending.sessionId;
    navigate(`/conversation/${pending.sessionId}`, { replace: true });
    void (async () => {
      try {
        if (!pending.isNew) await chat.loadSession(pending.sessionId);
        await chat.sendMessage(
          pending.sessionId,
          pending.query,
          pending.product,
          pending.version,
          pending.isNew,
        );
      } finally {
        resumingSessionRef.current = null;
      }
      void refreshSessions();
    })();
  }, []);

  useEffect(() => {
    if (!currentSessionId) return;
    if (resumingSessionRef.current === currentSessionId) return;
    if (newSessionIdRef.current === currentSessionId) {
      newSessionIdRef.current = null;
      return;
    }
    (async () => {
      try {
        await chat.loadSession(currentSessionId);
      } catch {
        navigate('/assistant', { replace: true });
      }
    })();
  }, [currentSessionId, chat.loadSession, navigate]);

  const handleNewChat = useCallback(() => {
    if (!showChat || isChatExiting) return;
    setIsChatExiting(true);
    setTimeout(() => {
      chat.reset();
      setIsChatExiting(false);
      navigate('/assistant');
    }, 280);
  }, [chat, showChat, isChatExiting, navigate]);

  const handleSelectSession = useCallback(
    (sessionId: string) => {
      navigate(`/conversation/${sessionId}`);
    },
    [navigate],
  );

  const handleDeleteSession = useCallback(
    async (sessionId: string) => {
      await deleteSession(sessionId);
      if (sessionId === currentSessionId) {
        chat.reset();
        navigate('/assistant', { replace: true });
      }
    },
    [deleteSession, currentSessionId, chat, navigate],
  );

  const handleSend = useCallback(
    async (
      query: string,
      product: string | null,
      attachments?: ImageRef[],
      draftSessionId?: string,
    ) => {
      const isNew = currentSessionId === null;
      const sid = currentSessionId ?? draftSessionId ?? crypto.randomUUID();
      if (isNew) {
        // Clear any messages left in memory from a previous conversation before
        // starting a fresh session. Navigating to the welcome screen (e.g. via the
        // wordmark) does not reset chat state, so without this the new message would
        // be appended to the prior conversation's messages.
        chat.reset();
        newSessionIdRef.current = sid;
        navigate(`/conversation/${sid}`);
      }
      // The picked version belongs to the picked product; anything else leaves the
      // choice to the server's default.
      const version = isNew && product === selectedSlug ? selectedVersion : null;
      await chat.sendMessage(sid, query, product, version, isNew, attachments ?? []);
      void refreshSessions();
    },
    [currentSessionId, chat, refreshSessions, navigate, selectedSlug, selectedVersion],
  );

  const handleStop = useCallback(() => {
    if (currentSessionId) void chat.stopGeneration(currentSessionId);
  }, [currentSessionId, chat.stopGeneration]);

  const isWelcomeExiting = showChat && !displayChat;

  return (
    <div className={styles.app}>
      <Toast
        visible={toastVisible}
        onDismiss={() => {
          setToastVisible(false);
          setToastSession(null);
        }}
        sessionId={toastSession?.session_id ?? null}
        sessionTitle={toastSession?.title ?? null}
        onNavigate={(id) => {
          setToastVisible(false);
          setToastSession(null);
          navigate(`/conversation/${id}`);
        }}
      />
      <Sidebar
        sessions={sessions}
        sessionsLoading={sessionsLoading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        currentSessionId={currentSessionId}
        collapsed={collapsed}
        width={sidebarWidth}
        onToggle={() => setCollapsed((c) => !c)}
        onWidthChange={setSidebarWidth}
        onNewChat={handleNewChat}
        onSelectSession={handleSelectSession}
        onRenameSession={renameSession}
        onDeleteSession={handleDeleteSession}
        onSearch={search}
        onLoadMore={loadMore}
        user={user}
        whatsNew={
          whatsNew.notes.length > 0
            ? { unread: whatsNew.unread.length > 0, onOpen: whatsNew.open }
            : undefined
        }
      />
      <main className={styles.main}>
        {displayChat ? (
          <ChatArea
            sessionId={currentSessionId ?? ''}
            messages={chat.messages}
            isLoadingSession={chat.isLoadingSession}
            status={chat.status}
            statusMessage={chat.statusMessage}
            activeSearches={chat.activeSearches}
            progress={chat.progress}
            streamingContent={chat.streamingContent}
            streamingCitations={chat.streamingCitations}
            product={chat.product}
            version={chat.version}
            error={chat.error}
            onSend={handleSend}
            onStop={handleStop}
            isExiting={isChatExiting}
            attachmentsEnabled={attachmentsEnabled}
            products={products}
            comingSoon={comingSoon}
            catalogLoading={catalogLoading}
            selectedSlug={selectedSlug}
            onSelectProduct={selectProduct}
            selectedVersion={selectedVersion}
            onContextPanelChange={setContextPanelOpen}
          />
        ) : (
          <WelcomeScreen
            onSend={handleSend}
            isExiting={isWelcomeExiting}
            user={user}
            attachmentsEnabled={attachmentsEnabled}
            products={products}
            comingSoon={comingSoon}
            catalogLoading={catalogLoading}
            catalogError={catalogError}
            selectedSlug={selectedSlug}
            onSelectProduct={selectProduct}
            selectedVersion={selectedVersion}
            notice={<WhatsNewPill unread={whatsNew.unread} onOpen={whatsNew.open} />}
          />
        )}
      </main>
      <FeedbackButton contextPanelOpen={contextPanelOpen} />
      <WhatsNewDialog
        open={whatsNew.isOpen}
        onClose={whatsNew.close}
        notes={whatsNew.notes}
        newIds={whatsNew.newIds}
      />
      <OnboardingSurvey
        questions={survey.questions}
        title={survey.title}
        intro={survey.intro}
        submitting={survey.submitting}
        onSubmit={survey.submit}
      />
    </div>
  );
}
